// Result view for the WebUI MVP.
//
// Displays the calculation-type label, totals / premium / fee / rent
// summary, validation messages, Markdown preview, item-result table,
// factor-chain table, and the market-price source metadata.

import React, { useMemo } from "react";
import {
  Card,
  Col,
  Descriptions,
  Row,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  Alert,
  Empty,
} from "antd";
import type { ColumnsType } from "antd/es/table";

import type { RunResult } from "../adapters/runFile.js";
import {
  availableExports,
  downloadExport,
  type ExportFormat,
} from "../adapters/downloadExport.js";
import { Button } from "antd";

const { Title, Paragraph, Text } = Typography;

export interface ResultViewProps {
  readonly result: RunResult;
  readonly sourceFileName: string;
  readonly inlineMarkdown: string;
}

export function ResultView({
  result,
  sourceFileName,
  inlineMarkdown,
}: ResultViewProps): JSX.Element {
  const exports = useMemo(() => availableExports(result), [result]);

  const handleDownload = async (format: ExportFormat): Promise<void> => {
    try {
      await downloadExport(result, format, sourceFileName);
    } catch (e) {
      const reason = e instanceof Error ? e.message : String(e);
      // eslint-disable-next-line no-alert
      alert(`导出失败：${reason}`);
    }
  };

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Card
        title={
          <Space>
            <Tag color="blue">{result.calculationType}</Tag>
            <Text type="secondary">来源：{sourceFileName}</Text>
          </Space>
        }
        extra={
          <Space>
            {exports.map((p) => (
              <Button
                key={p.format}
                onClick={() => void handleDownload(p.format)}
              >
                {p.label}
              </Button>
            ))}
          </Space>
        }
      >
        <SummaryPanel result={result} />
        {result.note && (
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 12 }}
            message={result.note}
          />
        )}
      </Card>

      {result.validationMessages.length > 0 && (
        <Card title="校验消息" size="small">
          <ValidationList messages={result.validationMessages} />
        </Card>
      )}

      {result.marketPrices && <PriceSourceCard prices={result.marketPrices} />}

      {result.itemResults && result.itemResults.length > 0 && (
        <Card title="分项测算结果" size="small">
          <ItemTable items={result.itemResults} />
        </Card>
      )}

      {result.itemResults && result.itemResults.length > 0 && (
        <Card title="系数链" size="small">
          <FactorChainTable items={result.itemResults} />
        </Card>
      )}

      <Card title="Markdown 报告" size="small">
        <Paragraph
          copyable
          style={{
            whiteSpace: "pre-wrap",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: 12,
            maxHeight: 480,
            overflow: "auto",
            background: "#fafafa",
            padding: 12,
            borderRadius: 4,
          }}
        >
          {inlineMarkdown}
        </Paragraph>
      </Card>
    </Space>
  );
}

// ---------------------------------------------------------------------------
// Sub-panels
// ---------------------------------------------------------------------------

function SummaryPanel({ result }: { result: RunResult }): JSX.Element {
  const items: { key: string; label: string; value: string | number }[] = [];
  if (result.totalLandPrice !== undefined) {
    items.push({ key: "tp", label: "宗地总地价 (元)", value: fmt(result.totalLandPrice) });
  }
  if (result.premium !== undefined) {
    items.push({ key: "pr", label: "应补缴地价 (元)", value: fmt(result.premium) });
  }
  if (result.totalFee !== undefined) {
    items.push({ key: "fee", label: "应付费用 (元)", value: fmt(result.totalFee) });
  }
  if (result.annualRent !== undefined) {
    items.push({ key: "ar", label: "年租金 (元)", value: fmt(result.annualRent) });
  }
  if (result.twentyYearLandPrice !== undefined) {
    items.push({
      key: "t20",
      label: "20 年期宗地地价 (元)",
      value: fmt(result.twentyYearLandPrice),
    });
  }
  if (result.capacityIncreasePremium !== undefined) {
    items.push({
      key: "cap",
      label: "容积提高应补地价合计 (元)",
      value: fmt(result.capacityIncreasePremium),
    });
  }
  if (result.totalChargeableGfa !== undefined) {
    items.push({
      key: "gfa",
      label: "计价建筑面积 (㎡)",
      value: fmt(result.totalChargeableGfa),
    });
  }
  if (result.averageFloorLandPrice !== undefined) {
    items.push({
      key: "avg",
      label: "综合楼面地价 (元/㎡)",
      value: fmt(result.averageFloorLandPrice),
    });
  }

  if (items.length === 0) {
    return <Empty description="无可汇总数据" />;
  }
  return (
    <Row gutter={[16, 16]}>
      {items.map((it) => (
        <Col key={it.key} xs={12} sm={8} md={6}>
          <Card size="small" style={{ background: "#f5f5f5" }}>
            <div style={{ fontSize: 12, color: "#888" }}>{it.label}</div>
            <div
              style={{
                fontSize: 18,
                fontWeight: 600,
                marginTop: 4,
                wordBreak: "break-all",
              }}
            >
              {it.value}
            </div>
          </Card>
        </Col>
      ))}
    </Row>
  );
}

function ValidationList({
  messages,
}: {
  messages: readonly { level: string; field?: string; message: string; suggestion?: string }[];
}): JSX.Element {
  return (
    <Tabs
      size="small"
      items={[
        {
          key: "all",
          label: `全部 (${messages.length})`,
          children: <ValidationTable messages={messages} />,
        },
        {
          key: "error",
          label: `错误 (${messages.filter((m) => m.level === "error").length})`,
          children: (
            <ValidationTable
              messages={messages.filter((m) => m.level === "error")}
            />
          ),
        },
        {
          key: "warning",
          label: `警告 (${messages.filter((m) => m.level === "warning").length})`,
          children: (
            <ValidationTable
              messages={messages.filter((m) => m.level === "warning")}
            />
          ),
        },
        {
          key: "info",
          label: `提示 (${messages.filter((m) => m.level === "info").length})`,
          children: (
            <ValidationTable
              messages={messages.filter((m) => m.level === "info")}
            />
          ),
        },
      ]}
    />
  );
}

function ValidationTable({
  messages,
}: {
  messages: readonly { level: string; field?: string; message: string; suggestion?: string }[];
}): JSX.Element {
  const cols: ColumnsType<{
    level: string;
    field?: string;
    message: string;
    suggestion?: string;
  }> = [
    {
      title: "级别",
      dataIndex: "level",
      width: 80,
      render: (lvl: string) => {
        if (lvl === "error") return <Tag color="red">错误</Tag>;
        if (lvl === "warning") return <Tag color="orange">警告</Tag>;
        return <Tag color="blue">提示</Tag>;
      },
    },
    { title: "字段", dataIndex: "field", width: 220 },
    { title: "消息", dataIndex: "message" },
    { title: "建议", dataIndex: "suggestion", width: 280 },
  ];
  return (
    <Table
      rowKey={(_r, idx) => String(idx)}
      size="small"
      dataSource={[...messages]}
      columns={cols}
      pagination={false}
    />
  );
}

function PriceSourceCard({
  prices,
}: {
  prices: NonNullable<RunResult["marketPrices"]>;
}): JSX.Element {
  const rows = [
    { label: "官方来源", value: prices.marketPriceSource },
    { label: "发布年份", value: prices.marketPriceYear },
    { label: "评估日期", value: prices.valuationDate },
    { label: "生效日期", value: prices.effectiveDate },
    { label: "标准宗地号", value: prices.standardParcelCode },
    { label: "区段编号", value: prices.benchmarkZoneCode },
    { label: "官方查询 URL", value: prices.officialQueryUrl },
    { label: "来源备注", value: prices.priceSourceRemark },
    { label: "价格来源（旧）", value: prices.priceSource },
    { label: "价格时点（旧）", value: prices.priceDate },
  ].filter((r) => r.value !== undefined && r.value !== "");

  if (rows.length === 0) return <></>;
  return (
    <Card title="市场价格来源说明" size="small">
      <Descriptions size="small" column={2} bordered>
        {rows.map((r) => (
          <Descriptions.Item key={r.label} label={r.label}>
            {String(r.value)}
          </Descriptions.Item>
        ))}
      </Descriptions>
    </Card>
  );
}

interface ItemRow {
  readonly itemId: string;
  readonly buildingType: string;
  readonly gfa: number;
  readonly chargeableGfa: number;
  readonly applicablePriceType: string;
  readonly marketPrice: number;
  readonly finalFactor: number;
  readonly itemLandPrice: number;
  readonly floorLandPrice: number;
}

function ItemTable({ items }: { items: readonly ItemRow[] }): JSX.Element {
  const cols: ColumnsType<ItemRow> = [
    { title: "分项ID", dataIndex: "itemId", width: 110 },
    { title: "建筑类型", dataIndex: "buildingType", width: 110 },
    { title: "建筑面积 (㎡)", dataIndex: "gfa", width: 110 },
    { title: "计价面积 (㎡)", dataIndex: "chargeableGfa", width: 110 },
    { title: "适用价格类型", dataIndex: "applicablePriceType", width: 110 },
    { title: "市场价格 (元/㎡)", dataIndex: "marketPrice", width: 110 },
    { title: "综合系数", dataIndex: "finalFactor", width: 90 },
    { title: "分项地价 (元)", dataIndex: "itemLandPrice", width: 140 },
    { title: "楼面地价 (元/㎡)", dataIndex: "floorLandPrice", width: 140 },
  ];
  return (
    <Table
      rowKey="itemId"
      size="small"
      dataSource={[...items]}
      columns={cols}
      pagination={false}
    />
  );
}

function FactorChainTable({ items }: { items: readonly ItemRow[] }): JSX.Element {
  const cols: ColumnsType<ItemRow> = [
    { title: "分项ID", dataIndex: "itemId", width: 110 },
    { title: "建筑类型", dataIndex: "buildingType", width: 110 },
    { title: "综合系数", dataIndex: "finalFactor", width: 110 },
    { title: "分项地价 (元)", dataIndex: "itemLandPrice", width: 140 },
  ];
  return (
    <Table
      rowKey="itemId"
      size="small"
      dataSource={[...items]}
      columns={cols}
      pagination={false}
    />
  );
}

function fmt(n: number): string {
  if (!Number.isFinite(n)) return String(n);
  return String(Math.round(n * 1_000_000) / 1_000_000);
}
